<template>
  <div
    ref="rootRef"
    :class="rootClasses"
    :aria-busy="props.loading || undefined"
    data-cy="ds-title-field"
  >
    <div :class="DS_TITLE_FIELD_CLASSES.Interactive">
      <DSTitleFieldDisplay
        v-if="!isEditing"
        ref="displayControlRef"
        :model-value="props.modelValue"
        :placeholder="props.placeholder"
        :ariaLabel="props.ariaLabel"
        :disabled="props.disabled"
        :error-message-ids="errorMessageIds"
        @activate="startEditing"
        @focus="onFocus"
        @blur="onBlur"
      />
      <DSTitleFieldEditor
        v-else
        ref="editorRef"
        :model-value="props.modelValue"
        :placeholder="props.placeholder"
        :maxlength="props.maxlength"
        :loading="props.loading"
        :ariaLabel="props.ariaLabel"
        :error-message-ids="errorMessageIds"
        @save="emit('save', $event)"
        @cancel="cancelEditing"
        @focus="onFocus"
        @blur="onBlur"
      />
    </div>

    <DSTitleFieldErrors
      v-if="hasErrors"
      :error-messages="props.errorMessages"
      :error-message-id-prefix="errorMessageIdPrefix"
    />
  </div>
</template>

<script setup lang="ts">
import { onUpdated, ref, watch } from "vue";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import DSTitleFieldDisplay from "@/src/design_system/form_elements/title_field/components/DSTitleFieldDisplay.vue";
import DSTitleFieldEditor from "@/src/design_system/form_elements/title_field/components/DSTitleFieldEditor.vue";
import DSTitleFieldErrors from "@/src/design_system/form_elements/title_field/components/DSTitleFieldErrors.vue";
import { useDSTitleField } from "@/src/design_system/form_elements/title_field/composables/useDSTitleField";
import { DS_TITLE_FIELD_CLASSES } from "@/src/design_system/form_elements/title_field/constants/classes";
import type { DSTitleFieldEmits, DSTitleFieldProps } from "@/src/design_system/form_elements/title_field/types";

const props = withDefaults(defineProps<DSTitleFieldProps>(), {
  modelValue: "",
  editing: undefined,
  defaultEditing: false,
  disabled: false,
  loading: false,
});

const emit = defineEmits<DSTitleFieldEmits>();
const rootRef = ref<HTMLElement>();
const displayControlRef = ref<InstanceType<typeof DSTitleFieldDisplay>>();
const editorRef = ref<InstanceType<typeof DSTitleFieldEditor>>();

const {
  activeControl,
  cancelEditing,
  errorMessageIdPrefix,
  errorMessageIds,
  hasErrors,
  isEditing,
  rootClasses,
  startEditing,
} = useDSTitleField({ props, emit, displayControlRef, editorRef });

const { focus, blur } = useDSInput(activeControl);

let hasFocus = false;
let replacingControl = false;
watch(
  isEditing,
  () => {
    replacingControl = true;
  },
  { flush: "sync" }
);
onUpdated(() => {
  replacingControl = false;
});
const onFocus = (event: FocusEvent) => {
  if (hasFocus) return;
  hasFocus = true;
  emit("focus", event);
};
const onBlur = (event: FocusEvent) => {
  const leavesRoot = event.relatedTarget instanceof Node && !rootRef.value?.contains(event.relatedTarget);
  // Chromium emits blur when the focused display/editor is removed during a mode switch.
  if (replacingControl && !props.disabled && !leavesRoot) return;
  hasFocus = false;
  emit("blur", event);
};

watch(
  () => props.disabled,
  disabled => {
    if (disabled) hasFocus = false;
  }
);

defineExpose({ focus, blur });
</script>
